/**
 * Shared basket/topic search matching — same logic as BSI (bsi_search_performed).
 * Connections result ranking is separate (getConnectionsSearchResult) and must not
 * change the BSI helpers below.
 */

export const HUB_SIZE_CAP = 10;

export const getSearchPriority = (basket, searchTerm) => {
  if (!searchTerm) return 0;

  const lowercasedSearchTerm = searchTerm.toLowerCase();

  if (basket.name.toLowerCase().includes(lowercasedSearchTerm)) {
    return 1;
  }

  if (basket.topics?.some((topic) =>
    topic.name.toLowerCase().includes(lowercasedSearchTerm)
  )) {
    return 2;
  }

  if (basket.topics?.some((topic) =>
    topic.concepts?.some((concept) =>
      concept.text.toLowerCase().includes(lowercasedSearchTerm)
    )
  )) {
    return 3;
  }

  return 4;
};

export const isBasketMatch = (basket, searchTerm) => {
  if (!searchTerm?.trim()) return false;

  const lowercasedSearchTerm = searchTerm.toLowerCase();

  if (basket.name.toLowerCase().includes(lowercasedSearchTerm)) return true;

  if (basket.topics?.some((topic) =>
    topic.name.toLowerCase().includes(lowercasedSearchTerm)
  )) return true;

  return Boolean(
    basket.topics?.some((topic) =>
      topic.concepts?.some((concept) =>
        concept.text.toLowerCase().includes(lowercasedSearchTerm)
      )
    )
  );
};

/** Topic matches name or any concept text (not basket-name-only). */
export const topicMatchesSearch = (topic, searchTerm) => {
  if (!searchTerm?.trim() || !topic) return false;
  const lowercasedSearchTerm = searchTerm.toLowerCase();

  if (topic.name?.toLowerCase().includes(lowercasedSearchTerm)) return true;

  return Boolean(
    topic.concepts?.some((concept) =>
      concept.text?.toLowerCase().includes(lowercasedSearchTerm)
    )
  );
};

export const topicNameMatches = (topic, searchTerm) => {
  if (!searchTerm?.trim() || !topic?.name) return false;
  return topic.name.toLowerCase().includes(searchTerm.trim().toLowerCase());
};

export const topicConceptMatches = (topic, searchTerm) => {
  if (!searchTerm?.trim() || !topic) return false;
  const lower = searchTerm.trim().toLowerCase();
  return Boolean(
    topic.concepts?.some((concept) => concept.text?.toLowerCase().includes(lower))
  );
};

const toTime = (iso) => {
  if (!iso) return 0;
  const time = new Date(iso).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortByRecencyDesc = (items) =>
  [...items].sort((a, b) => toTime(b.updatedAt) - toTime(a.updatedAt));

const buildBasketResultItem = (basket, matchedTopicId) => {
  const topics = basket.topics || [];
  return {
    kind: 'basket',
    id: basket.id,
    name: basket.name,
    updatedAt: basket.updated_at || null,
    topics,
    matchedTopicId,
    oversized: topics.length > HUB_SIZE_CAP,
    is_locked: Boolean(basket.is_locked),
  };
};

const buildStandaloneResultItem = (topic) => ({
  kind: 'standalone',
  id: topic.id,
  name: topic.name,
  updatedAt: topic.updated_at || null,
  topic,
  is_locked: Boolean(topic.is_locked),
});

/**
 * Resolve matchedTopicId for a basket hub open:
 * first name-matching topic, else first concept-matching, else null.
 */
export const resolveMatchedTopicId = (topics, searchTerm) => {
  const list = topics || [];
  const byName = list.find((t) => topicNameMatches(t, searchTerm));
  if (byName) return byName.id;
  const byConcept = list.find((t) => topicConceptMatches(t, searchTerm));
  if (byConcept) return byConcept.id;
  return null;
};

/**
 * Connections two-tier search (v5). Does not alter BSI helpers above.
 * @returns {{ type: 'empty' } | { type: 'zero' } | { type: 'results', tier1: ResultItem[], tier2: ResultItem[] }}
 */
export const getConnectionsSearchResult = (baskets, unfiledTopics, searchTerm) => {
  const trimmed = searchTerm?.trim() ?? '';
  if (!trimmed) {
    return { type: 'empty' };
  }

  const lower = trimmed.toLowerCase();
  const tier1ByKey = new Map();
  const tier2ByKey = new Map();

  const addTier1 = (item) => {
    const key = `${item.kind}:${item.id}`;
    if (!tier1ByKey.has(key)) tier1ByKey.set(key, item);
  };

  const addTier2 = (item) => {
    const key = `${item.kind}:${item.id}`;
    if (tier1ByKey.has(key) || tier2ByKey.has(key)) return;
    tier2ByKey.set(key, item);
  };

  // --- Baskets ---
  (baskets || []).forEach((basket) => {
    const topics = basket.topics || [];
    const basketNameMatch = basket.name?.toLowerCase().includes(lower);
    const nameMatchTopics = topics.filter((t) => topicNameMatches(t, trimmed));
    const conceptMatchTopics = topics.filter((t) => topicConceptMatches(t, trimmed));

    if (basketNameMatch || nameMatchTopics.length > 0) {
      addTier1(
        buildBasketResultItem(basket, resolveMatchedTopicId(topics, trimmed))
      );
      return;
    }

    // Concept-only: basket not already in tier 1
    if (conceptMatchTopics.length > 0) {
      addTier2(
        buildBasketResultItem(basket, conceptMatchTopics[0].id)
      );
    }
  });

  // --- Unfiled / standalone topics ---
  (unfiledTopics || []).forEach((topic) => {
    if (topicNameMatches(topic, trimmed)) {
      addTier1(buildStandaloneResultItem(topic));
      return;
    }
    if (topicConceptMatches(topic, trimmed)) {
      addTier2(buildStandaloneResultItem(topic));
    }
  });

  const tier1 = sortByRecencyDesc([...tier1ByKey.values()]);
  const tier2 = sortByRecencyDesc([...tier2ByKey.values()]);

  if (tier1.length === 0 && tier2.length === 0) {
    return { type: 'zero' };
  }

  return { type: 'results', tier1, tier2 };
};

/**
 * BSI grid: matching baskets first (by priority), then non-matches by oldest updated_at.
 */
export const partitionBasketsBySearch = (baskets, searchTerm) => {
  if (!searchTerm?.trim()) {
    return { matching: [], nonMatching: [...(baskets || [])], matchingBasketCount: 0 };
  }

  const matching = [];
  const nonMatching = [];

  (baskets || []).forEach((basket) => {
    if (isBasketMatch(basket, searchTerm)) matching.push(basket);
    else nonMatching.push(basket);
  });

  matching.sort((a, b) => {
    const priorityA = getSearchPriority(a, searchTerm);
    const priorityB = getSearchPriority(b, searchTerm);
    if (priorityA !== priorityB) return priorityA - priorityB;
    return a.name.localeCompare(b.name);
  });

  const basketToTime = (basket) => {
    const time = basket.updated_at ? new Date(basket.updated_at).getTime() : NaN;
    return Number.isNaN(time) ? 0 : time;
  };
  nonMatching.sort((a, b) => basketToTime(a) - basketToTime(b));

  return {
    matching,
    nonMatching,
    matchingBasketCount: matching.length,
  };
};
